// What a package changes, read against the board it is waiting on — t-159.
//
// One shape for all four places the mockup draws a diff: the confirmation
// modal (§3), the expanded banner with its per-row cancel (§5), the muted list
// a "Save now" collision prints (§6) and the refused package (§8). They differ
// in what they hang off a row, never in what a row IS.

import {
  consequenceFor,
  fieldApiName,
  fieldLabel,
  fieldSection,
  fieldValueNote,
  formatFieldValue,
  NOT_SET,
  SECTION_ORDER,
  wasCreateOnly,
  type SectionName,
} from "./pendingFields";
import { diffRewardTables, summariseRewardsDiff } from "./rewardsDiff";
import type { Guards, Leaderboard, RewardSpec } from "./types";

export interface DiffRow {
  /**
   * The PACKAGE key this row belongs to — what a per-row cancel removes. Not
   * always the same as `api`: the three guards are three rows of one `guards`
   * column, and taking one out takes the column.
   */
  field: string;
  label: string;
  api: string;
  /** The board's value today, already formatted. */
  oldText: string;
  /** The board holds nothing there — drawn without a strike-through, because
     there is no old value to cross out. */
  oldMissing: boolean;
  newText: string;
  /** The muted note at the end of the values: "was settable only at creation",
     "required by the new kind", the tier keys. */
  note?: string;
  /** What the operator will see happen — only where that is not the field. */
  consequence?: string;
  /** This is the row the cut refused (failed packages only). */
  blame?: boolean;
}

export interface DiffGroup {
  section: SectionName;
  rows: DiffRow[];
}

/** Every key the diff walks, in the order the form shows them. Written out
   rather than derived from the package so that two packages with the same
   fields always list them the same way. */
const FIELD_ORDER = [
  "name",
  "description",
  "visibility",
  "operator",
  "sort",
  "submit_policy",
  "realtime",
  "guild_score_source",
  "guild_aggregator",
  "guild_stat_key",
  "min_guild_members",
  "reward_contributors_only",
  "late_joiners_may_score",
  "window_kind",
  "cron",
  "window_minutes",
  "event_start_at",
  "event_end_at",
  "scope",
  "segmentation",
  "tier_mode",
  "tier_config",
  "bucket_size",
  "roster_size",
  "matchmaking_config",
  "country_split",
  "auto_enroll",
  "ladder_decision",
  "guards",
  "hold_finished_cycles",
  "retention_days",
  "metadata",
  "rewards",
];

const GUARD_KEYS: (keyof Guards)[] = [
  "max_score_per_submit",
  "max_delta_per_submit",
  "min_interval_sec",
];

/** The board's stored value for a package key. `ladder_decision` has none —
   it is a decision about players, not a column the board carries. */
function boardValue(lb: Leaderboard, field: string): unknown {
  if (field === "ladder_decision") return undefined;
  return (lb as unknown as Record<string, unknown>)[field];
}

/**
 * `undefined` and `null` are the same answer here — "the board holds nothing
 * there" — and an empty string is that answer too, since every text column on
 * this form defaults to it. Distinguishing them would call a board with no
 * description one whose description just changed.
 */
function isMissing(v: unknown): boolean {
  return v === undefined || v === null || v === "";
}

/**
 * JSON with the keys of every object sorted, so two objects that say the same
 * thing compare equal whatever order they were built in. The board's `guards`
 * come off the wire in the server's order and the form rebuilds them in its
 * own; without this every save of an untouched board would report the guards
 * as changed.
 *
 * Array order is left alone on purpose — the tier ladder IS its order.
 */
function stableStringify(v: unknown): string {
  if (v === null || typeof v !== "object") return JSON.stringify(v ?? null) ?? "null";
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`;
  const entries = Object.entries(v as Record<string, unknown>)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, value]) => `${JSON.stringify(k)}:${stableStringify(value)}`).join(",")}}`;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (isMissing(a) && isMissing(b)) return true;
  if (typeof a === "object" || typeof b === "object") {
    return stableStringify(a ?? null) === stableStringify(b ?? null);
  }
  return a === b;
}

/** The note the mockup pins to a row whose presence is owed to another field
   in the same package. */
function dependencyNote(
  field: string,
  fields: Record<string, unknown>,
): string | undefined {
  if (field === "window_minutes" && "window_kind" in fields) {
    return "required by the new kind";
  }
  if (field === "tier_mode" && "segmentation" in fields) {
    return "required by the new segmentation";
  }
  if (field === "tier_config" && "segmentation" in fields) {
    return undefined;
  }
  return undefined;
}

/**
 * Said by the change that takes the board's last table away. The flag itself
 * is never a row: the form does not send it there and the server resets it
 * (clearDependents), so without this line the reset is silent.
 */
export const REALTIME_OFF_CONSEQUENCE =
  "Realtime switches off: without rooms or scope guild_internal there is no table to send rank.changed to.";

function realtimeAllowed(bucketSize: unknown, scope: unknown): boolean {
  return !isMissing(bucketSize) || scope === "guild_internal";
}

/**
 * Which field of `fields` switches the board's realtime off, or undefined when
 * none does. Mirrors clearDependents: segmentation none/personal takes the
 * rooms with it unless bucket_size is named too, and a write that names
 * realtime is judged rather than reset.
 */
export function realtimeOffCause(
  fields: Record<string, unknown>,
  lb: Leaderboard,
): string | undefined {
  if (lb.realtime !== true || fields.realtime !== undefined) return undefined;
  if (!realtimeAllowed(lb.bucket_size, lb.scope)) return undefined;
  const scope = "scope" in fields ? fields.scope : lb.scope;
  const bucketSize =
    "bucket_size" in fields
      ? fields.bucket_size
      : fields.segmentation === "none" || fields.segmentation === "personal"
        ? null
        : lb.bucket_size;
  if (realtimeAllowed(bucketSize, scope)) return undefined;
  // On guild_internal the scope is what went, whatever the rooms did.
  if (lb.scope === "guild_internal") return "scope";
  return "bucket_size" in fields ? "bucket_size" : "segmentation";
}

export interface BuildDiffOptions {
  /** The field the cut refused — highlighted, with its own tag. */
  blameField?: string;
  /** The board's prize table as it stands, so a package that carries one can
     say "12 rows → 14 rows" instead of naming only the new count. */
  currentRewards?: unknown[];
  /**
   * Keep a row whose value already matches the board.
   *
   * For a package still WAITING, a matching value is noise — see buildDiff.
   * For a RESOLVED one it is the normal state and dropping it empties the
   * diff: an applied package's values are on the board by definition, so the
   * rule that serves a promise turns the record of a kept promise into
   * "0 changes". A resolved package's rows are a record of what it carried,
   * not a prediction, so nothing about the board can take one away.
   */
  keepUnchanged?: boolean;
}

function rowFor(
  field: string,
  next: unknown,
  lb: Leaderboard,
  fields: Record<string, unknown>,
  opts: BuildDiffOptions,
): DiffRow {
  const old = field === "rewards" ? opts.currentRewards : boardValue(lb, field);
  const note =
    dependencyNote(field, fields) ??
    (wasCreateOnly(field) ? "was settable only at creation" : undefined) ??
    // "14 rows" alone says how big the new table is, not what moved in it —
    // and a replacement is the easiest way to lose a prize by accident. The
    // per-row breakdown lives on the Rewards tab; this is its tally.
    rewardsNote(field, old, next) ??
    fieldValueNote(field, next);
  const consequence = [
    consequenceFor(field, next, lb),
    realtimeOffCause(fields, lb) === field ? REALTIME_OFF_CONSEQUENCE : undefined,
  ]
    .filter(Boolean)
    .join(" ");
  return {
    field,
    label: fieldLabel(field),
    api: fieldApiName(field),
    oldText: formatFieldValue(field, old),
    oldMissing: isMissing(old),
    newText: formatFieldValue(field, next),
    note,
    consequence: consequence || undefined,
    blame: opts.blameField === field || undefined,
  };
}

/**
 * The package as rows, grouped by the form card each field is edited in. Rows
 * whose value already matches the board are dropped: a package can outlive an
 * immediate save of the same field, and a diff line reading `sum → sum` would
 * ask the operator to worry about nothing.
 *
 * That rule is about a package that has not happened yet. A resolved one sets
 * `keepUnchanged` and keeps every row it carried — see BuildDiffOptions.
 */
export function buildDiff(
  fields: Record<string, unknown>,
  lb: Leaderboard,
  opts: BuildDiffOptions = {},
): DiffGroup[] {
  const rows: DiffRow[] = [];
  const known = new Set(FIELD_ORDER);
  const order = [
    ...FIELD_ORDER,
    ...Object.keys(fields).filter((f) => !known.has(f)),
  ];
  for (const field of order) {
    if (!(field in fields)) continue;
    const next = fields[field];
    if (field === "guards") {
      const before = (lb.guards ?? {}) as Record<string, unknown>;
      const after = (next ?? {}) as Record<string, unknown>;
      for (const key of GUARD_KEYS) {
        // A guard the package never named is not a row of it, whichever mode
        // this is: under `keepUnchanged` the equality test below would stop
        // being the thing that hides it, and the diff would grow three
        // `not set → not set` lines nobody scheduled.
        if (!(key in after)) continue;
        if (!opts.keepUnchanged && sameValue(before[key], after[key])) continue;
        rows.push({
          field: "guards",
          label: fieldLabel(`guards.${key}`),
          api: `guards.${key}`,
          oldText: formatFieldValue(`guards.${key}`, before[key]),
          oldMissing: isMissing(before[key]),
          newText: formatFieldValue(`guards.${key}`, after[key]),
          blame: opts.blameField === "guards" || undefined,
        });
      }
      continue;
    }
    if (
      !opts.keepUnchanged &&
      field !== "ladder_decision" &&
      sameValue(boardValue(lb, field), next)
    ) {
      continue;
    }
    rows.push(rowFor(field, next, lb, fields, opts));
  }

  const groups = new Map<SectionName, DiffRow[]>();
  for (const row of rows) {
    const section = fieldSection(row.field);
    const bucket = groups.get(section);
    if (bucket) bucket.push(row);
    else groups.set(section, [row]);
  }
  return SECTION_ORDER.filter((s) => groups.has(s)).map((section) => ({
    section,
    rows: groups.get(section) ?? [],
  }));
}

/** How many changes the operator is being told about — rows, not keys, so the
   three guards count as the three lines the diff draws. */
export function countDiffRows(groups: DiffGroup[]): number {
  return groups.reduce((n, g) => n + g.rows.length, 0);
}

// --- The form's own diff ----------------------------------------------------

/**
 * Which fields the form is holding that the board does not.
 *
 * Only STATED values count. An absent key in a built payload means "leave the
 * column alone" everywhere else in this feature (see buildUpdatePayload), and
 * a field that vanished because the window kind moved under it is not a change
 * to that field — it is the same change, already counted once.
 */
export function changedFields(
  input: Record<string, unknown>,
  stored: Record<string, unknown>,
): string[] {
  const out: string[] = [];
  const known = new Set(FIELD_ORDER);
  const order = [
    ...FIELD_ORDER,
    ...Object.keys(input).filter((f) => !known.has(f)),
  ];
  for (const field of order) {
    if (!(field in input)) continue;
    const next = input[field];
    if (next === undefined) continue;
    if (sameValue(stored[field], next)) continue;
    out.push(field);
  }
  return out;
}

/** The rows a package would carry for a set of changed fields — the same
   builder the banner uses, fed from the form instead of from the server. */
export function pickFields(
  input: Record<string, unknown>,
  fields: string[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f in input) out[f] = input[f];
  }
  return out;
}

export type { RewardSpec };
export { NOT_SET };

/**
 * The prize-table row's tally: "3 added · 1 removed · 2 changed".
 *
 * Undefined for every other field, and for a replacement that turns out to
 * change nothing — a note saying so would be a row nobody needed to read.
 */
function rewardsNote(
  field: string,
  before: unknown,
  after: unknown,
): string | undefined {
  if (field !== "rewards" || !Array.isArray(after)) return undefined;
  const diff = diffRewardTables(
    (Array.isArray(before) ? before : []) as RewardSpec[],
    after as RewardSpec[],
  );
  return summariseRewardsDiff(diff) || undefined;
}
